import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../../components/feature/DashboardLayout';
import { getUser } from '../../utils/auth';
import { getEvents, queryLogs } from '../../utils/storage';
import { getActiveEventId } from '../../utils/event';
import type { ActivityLog, MedicalEvent } from '../../types';

const formatIST = (utcTimestamp: string): string => {
  try {
    return new Date(utcTimestamp).toLocaleString('en-IN', {
      timeZone: 'Asia/Kolkata',
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true,
    });
  } catch {
    return utcTimestamp;
  }
};

export default function ActivityLogsPage() {
  const navigate = useNavigate();
  const user = getUser();
  const isMaster = user?.role === 'master_admin';

  const [logs, setLogs] = useState<ActivityLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [events, setEvents] = useState<MedicalEvent[]>([]);
  const [scope, setScope] = useState<string>(getActiveEventId() || 'all');
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date());

  // Filters (sent to the server)
  const [username, setUsername] = useState('');
  const [role, setRole] = useState('');
  const [module, setModule] = useState('');
  const [testId, setTestId] = useState('');
  const [action, setAction] = useState('');
  const [patientId, setPatientId] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 50;

  useEffect(() => {
    if (!user) { navigate('/dashboard'); return; }
    getEvents().then((all) => setEvents(all));
    // Non-master users are locked to their active event scope.
    if (!isMaster) setScope(getActiveEventId());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const applyFilters = useCallback(async () => {
    setLoading(true);
    const data = await queryLogs({
      scope,
      username: username || undefined,
      role: role || undefined,
      module: module || undefined,
      testId: testId || undefined,
      action: action || undefined,
      patientId: patientId || undefined,
      from: fromDate || undefined,
      to: toDate || undefined,
    });
    setLogs(data);
    setLastUpdated(new Date());
    setCurrentPage(1);
    setLoading(false);
  }, [scope, username, role, module, testId, action, patientId, fromDate, toDate]);

  useEffect(() => {
    applyFilters();
  }, [applyFilters]);

  // Auto refresh every 20s
  useEffect(() => {
    const id = setInterval(() => applyFilters(), 20000);
    return () => clearInterval(id);
  }, [applyFilters]);

  const clearFilters = () => {
    setUsername(''); setRole(''); setModule(''); setTestId(''); setAction('');
    setPatientId(''); setFromDate(''); setToDate('');
  };

  const unique = (key: keyof ActivityLog) => Array.from(new Set(logs.map((l) => String(l[key] ?? '')).filter(Boolean))).sort();
  const usernames = unique('username');
  const roles = unique('role');
  const modules = unique('module');
  const tests = unique('testId');
  const actions = unique('action');

  const totalPages = Math.ceil(logs.length / pageSize);
  const currentLogs = logs.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  const handleExport = () => {
    const csv = [
      ['Time (IST)', 'User', 'Role', 'Module', 'Test', 'Action', 'Patient', 'Description', 'IP'].join(','),
      ...logs.map((l) => [
        formatIST(l.timestamp), l.username, l.role || '', l.module || '', l.testId || '',
        l.action, l.patientId || '', `"${(l.description || l.details || '').replace(/"/g, '""')}"`, l.ipAddress || '',
      ].join(',')),
    ].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `activity-logs-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const actionColor = (a: string) =>
    a === 'Login' ? 'bg-green-50 text-green-700'
    : a === 'Logout' ? 'bg-blue-50 text-blue-700'
    : a === 'Failed Login' ? 'bg-red-50 text-red-700'
    : 'bg-gray-50 text-gray-700';

  const filterCls = 'w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm';

  const scopeLabel = useMemo(() => {
    if (scope === 'all') return 'ALL ACTIVITY';
    const ev = events.find((e) => e.id === scope);
    return ev ? `${ev.eventName} (${ev.eventCode})` : scope.toUpperCase();
  }, [scope, events]);

  return (
    <DashboardLayout>
      <div className="p-6 space-y-6">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Activity Logs</h1>
            <p className="text-sm text-gray-500 mt-1">
              Scope: <span className="font-semibold text-teal-600">{scopeLabel}</span> • Updated{' '}
              {lastUpdated.toLocaleTimeString('en-IN', { timeZone: 'Asia/Kolkata', hour12: true })}
            </p>
          </div>
          <div className="flex items-center gap-3">
            {isMaster && (
              <select
                value={scope}
                onChange={(e) => setScope(e.target.value)}
                className={`${filterCls} cursor-pointer min-w-[180px]`}
              >
                <option value="all">ALL ACTIVITY</option>
                {events.map((ev) => (
                  <option key={ev.id} value={ev.id}>{ev.eventName} ({ev.eventCode})</option>
                ))}
              </select>
            )}
            <button onClick={applyFilters} className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors flex items-center gap-2 whitespace-nowrap">
              <i className="ri-refresh-line"></i>Refresh
            </button>
            <button onClick={handleExport} className="px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-2 whitespace-nowrap">
              <i className="ri-download-line"></i>Export
            </button>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div><label className="block text-xs font-medium text-gray-600 mb-1">User</label>
              <select value={username} onChange={(e) => setUsername(e.target.value)} className={`${filterCls} cursor-pointer`}>
                <option value="">All Users</option>
                {usernames.map((u) => <option key={u} value={u}>{u}</option>)}
              </select>
            </div>
            <div><label className="block text-xs font-medium text-gray-600 mb-1">Role</label>
              <select value={role} onChange={(e) => setRole(e.target.value)} className={`${filterCls} cursor-pointer`}>
                <option value="">All Roles</option>
                {roles.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
            <div><label className="block text-xs font-medium text-gray-600 mb-1">Module</label>
              <select value={module} onChange={(e) => setModule(e.target.value)} className={`${filterCls} cursor-pointer`}>
                <option value="">All Modules</option>
                {modules.map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
            <div><label className="block text-xs font-medium text-gray-600 mb-1">Test</label>
              <select value={testId} onChange={(e) => setTestId(e.target.value)} className={`${filterCls} cursor-pointer`}>
                <option value="">All Tests</option>
                {tests.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div><label className="block text-xs font-medium text-gray-600 mb-1">Action</label>
              <select value={action} onChange={(e) => setAction(e.target.value)} className={`${filterCls} cursor-pointer`}>
                <option value="">All Actions</option>
                {actions.map((a) => <option key={a} value={a}>{a}</option>)}
              </select>
            </div>
            <div><label className="block text-xs font-medium text-gray-600 mb-1">Patient ID</label>
              <input type="text" value={patientId} onChange={(e) => setPatientId(e.target.value)} className={filterCls} placeholder="Patient ID" />
            </div>
            <div><label className="block text-xs font-medium text-gray-600 mb-1">From Date</label>
              <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} className={filterCls} />
            </div>
            <div><label className="block text-xs font-medium text-gray-600 mb-1">To Date</label>
              <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} className={filterCls} />
            </div>
          </div>
          <div className="mt-4 pt-4 border-t border-gray-200 flex items-center justify-between gap-3 flex-wrap">
            <button onClick={clearFilters} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap">
              <i className="ri-close-line mr-1"></i>Clear Filters
            </button>
            <p className="text-sm text-gray-600">
              Showing <span className="font-semibold text-gray-900">{currentLogs.length}</span> of{' '}
              <span className="font-semibold text-gray-900">{logs.length}</span> logs
            </p>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Time</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">User</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Role</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Module</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Test</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Action</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Patient</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Description</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {loading ? (
                  <tr><td colSpan={8} className="px-4 py-10 text-center text-gray-400">Loading...</td></tr>
                ) : currentLogs.length === 0 ? (
                  <tr><td colSpan={8} className="px-4 py-10 text-center text-gray-500">No logs found</td></tr>
                ) : currentLogs.map((log) => (
                  <tr key={log.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">{formatIST(log.timestamp)}</td>
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">{log.username}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{log.role || '—'}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{log.module || '—'}</td>
                    <td className="px-4 py-3 text-sm text-gray-600"><span className="font-mono text-xs">{log.testId || '—'}</span></td>
                    <td className="px-4 py-3 text-sm">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${actionColor(log.action)}`}>{log.action}</span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600 font-mono">{log.patientId || '—'}</td>
                    <td className="px-4 py-3 text-sm text-gray-600 max-w-md truncate">{log.description || log.details}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {totalPages > 1 && (
            <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between">
              <div className="text-sm text-gray-600">Page {currentPage} of {totalPages}</div>
              <div className="flex items-center gap-2">
                <button onClick={() => setCurrentPage((p) => Math.max(1, p - 1))} disabled={currentPage === 1} className="px-3 py-1 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 transition-colors whitespace-nowrap">Previous</button>
                <button onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} className="px-3 py-1 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 transition-colors whitespace-nowrap">Next</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}